import { FitAddon } from '@xterm/addon-fit'
import { WebLinksAddon } from '@xterm/addon-web-links'
import { Terminal } from '@xterm/xterm'
import { KeyboardIcon } from 'lucide-react'
import React, { useEffect, useRef, useState, useCallback } from 'react'

import { urlDoSocketComSessao } from '@nebula/shared/services/consoleApi'

import { classificarMensagemDoSerial } from './serialConsoleMensagens'

import '@xterm/xterm/css/xterm.css'

interface SerialConsoleProps {
  websocketUrl: string
  instanceId?: string
  onConnect?: () => void
  onDisconnect?: () => void
  onError?: (error: Error) => void
  className?: string
}

const RECONNECT_DELAYS = [2000, 5000, 10000] // backoff: 2s, 5s, 10s
const KEEPALIVE_INTERVAL = 30000 // 30s — must be below proxy idle timeouts
const MAX_RECONNECT_ATTEMPTS = RECONNECT_DELAYS.length

const SerialConsole = React.forwardRef<any, SerialConsoleProps>(
  ({ websocketUrl, onConnect, onDisconnect, onError, className = '' }, ref) => {
    const terminalRef = useRef<HTMLDivElement>(null)
    const termRef = useRef<Terminal | null>(null)
    const fitAddonRef = useRef<FitAddon | null>(null)
    const wsRef = useRef<WebSocket | null>(null)
    const resizeObserverRef = useRef<ResizeObserver | null>(null)
    const keepaliveTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
    const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const reconnectAttemptRef = useRef(0)
    const intentionalCloseRef = useRef(false)
    // Cada connect/disconnect/limpeza avança a geração: um POST de ticket que volta depois disso é descartado.
    const geracaoRef = useRef(0)
    // A VM já mandou saída nesta conexão? Depois disso, marcador de erro é dado (#1078).
    const vmJaFalouRef = useRef(false)
    const [connected, setConnected] = useState(false)
    const [reconnecting, setReconnecting] = useState(false)
    const [idleDisconnected, setIdleDisconnected] = useState(false)

    const clearKeepAlive = useCallback(() => {
      if (keepaliveTimerRef.current) {
        clearInterval(keepaliveTimerRef.current)
        keepaliveTimerRef.current = null
      }
    }, [])

    const clearReconnectTimer = useCallback(() => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current)
        reconnectTimerRef.current = null
      }
    }, [])

    const startKeepAlive = useCallback(() => {
      clearKeepAlive()
      keepaliveTimerRef.current = setInterval(() => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          // Send empty termproxy data frame as keepalive
          wsRef.current.send('0:0:')
        }
      }, KEEPALIVE_INTERVAL)
    }, [clearKeepAlive])

    const disconnect = useCallback(() => {
      intentionalCloseRef.current = true
      geracaoRef.current++
      clearKeepAlive()
      clearReconnectTimer()
      if (wsRef.current) {
        try {
          wsRef.current.close()
        } catch {
          /* ignore */
        }
        wsRef.current = null
      }
      setConnected(false)
      setReconnecting(false)
      onDisconnect?.()
    }, [onDisconnect, clearKeepAlive, clearReconnectTimer])

    const connect = useCallback(async () => {
      if (!websocketUrl || !termRef.current) return

      // O JWT não vai na URL (#1079): o ticket de sessão é pedido a cada (re)conexão.
      const geracao = ++geracaoRef.current
      let wsUrl: string
      try {
        wsUrl = await urlDoSocketComSessao(websocketUrl)
      } catch {
        if (geracao !== geracaoRef.current) return
        const motivo = 'Não foi possível autorizar o console serial'
        termRef.current?.write(`\r\n\x1b[31m${motivo}\x1b[0m\r\n`)
        // Falha do ticket é uma tentativa perdida do backoff, não o fim da reconexão.
        if (reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay =
            RECONNECT_DELAYS[reconnectAttemptRef.current] ||
            RECONNECT_DELAYS[RECONNECT_DELAYS.length - 1]
          setReconnecting(true)
          reconnectTimerRef.current = setTimeout(() => {
            reconnectAttemptRef.current++
            connect()
          }, delay)
        } else {
          setReconnecting(false)
          onError?.(new Error(motivo))
        }
        return
      }
      if (geracao !== geracaoRef.current || !termRef.current) return

      const ws = new WebSocket(wsUrl)
      wsRef.current = ws
      intentionalCloseRef.current = false
      vmJaFalouRef.current = false

      ws.onopen = () => {
        setConnected(true)
        setReconnecting(false)
        setIdleDisconnected(false)
        reconnectAttemptRef.current = 0
        // O aviso pede Enter (#1088): sem o foco, ele fica no botão que abriu o serial (ou no
        // de reconectar) e o Enter reativa o botão em vez de chegar ao terminal. Só aqui, com
        // o socket aberto — antes disso o `onData` descarta a tecla.
        termRef.current?.focus()
        onConnect?.()
        startKeepAlive()
        // Send initial terminal size after connection
        if (termRef.current && fitAddonRef.current) {
          try {
            fitAddonRef.current.fit()
            const { cols, rows } = termRef.current
            ws.send(`1:${cols}:${rows}:`)
          } catch {
            /* ignore */
          }
        }
      }

      ws.onmessage = (event) => {
        if (termRef.current) {
          const handleData = (text: string) => {
            const mensagem = classificarMensagemDoSerial(text, { vmJaFalou: vmJaFalouRef.current })
            if (mensagem.tipo === 'ocioso') {
              setIdleDisconnected(true)
              intentionalCloseRef.current = true // don't auto-reconnect on idle timeout
              return
            }
            // Recusa definitiva (serial indisponível, autenticação) — don't auto-reconnect.
            // O motivo vai SEMPRE para o terminal: quem monta sem `onError` (o console do
            // Cloud Gateway) ficaria só com uma tela preta (#1078).
            if (mensagem.tipo === 'erro') {
              intentionalCloseRef.current = true
              geracaoRef.current++
              termRef.current?.write(`\r\n\x1b[31m${mensagem.mensagem}\x1b[0m\r\n`)
              onError?.(new Error(mensagem.mensagem))
              return
            }
            if (mensagem.texto) vmJaFalouRef.current = true
            termRef.current?.write(mensagem.texto)
          }

          if (typeof event.data === 'string') {
            handleData(event.data)
          } else if (event.data instanceof Blob) {
            event.data.text().then(handleData)
          } else if (event.data instanceof ArrayBuffer) {
            const decoder = new TextDecoder()
            handleData(decoder.decode(event.data))
          }
        }
      }

      ws.onclose = (event) => {
        clearKeepAlive()
        setConnected(false)
        onDisconnect?.()

        // Auth failure (4401) — don't auto-reconnect
        if (event.code === 4401) {
          intentionalCloseRef.current = true
          geracaoRef.current++
        }

        // Auto-reconnect unless intentionally closed or idle timeout
        if (!intentionalCloseRef.current && reconnectAttemptRef.current < MAX_RECONNECT_ATTEMPTS) {
          const delay =
            RECONNECT_DELAYS[reconnectAttemptRef.current] ||
            RECONNECT_DELAYS[RECONNECT_DELAYS.length - 1]

          setReconnecting(true)
          reconnectTimerRef.current = setTimeout(() => {
            reconnectAttemptRef.current++
            connect()
          }, delay)
        } else if (!intentionalCloseRef.current) {
          setReconnecting(false)
          onError?.(new Error('Falha ao reconectar ao console serial'))
        }
      }

      ws.onerror = (event) => {
        console.error('Serial console WebSocket error:', event)
        onError?.(new Error('WebSocket connection error'))
      }
    }, [websocketUrl, onConnect, onDisconnect, onError, startKeepAlive, clearKeepAlive])

    const reconnect = useCallback(() => {
      clearKeepAlive()
      clearReconnectTimer()
      if (wsRef.current) {
        try {
          wsRef.current.close()
        } catch {
          /* ignore */
        }
        wsRef.current = null
      }
      reconnectAttemptRef.current = 0
      setIdleDisconnected(false)
      setReconnecting(true)
      connect()
    }, [connect, clearKeepAlive, clearReconnectTimer])

    // Initialize terminal
    useEffect(() => {
      if (!terminalRef.current) return

      const terminal = new Terminal({
        cursorBlink: true,
        cursorStyle: 'block',
        fontFamily: "'Fira Code', 'Cascadia Code', 'JetBrains Mono', monospace",
        fontSize: 14,
        lineHeight: 1.2,
        theme: {
          background: '#1a1b26',
          foreground: '#a9b1d6',
          cursor: '#c0caf5',
          selectionBackground: '#33467c',
          black: '#15161e',
          red: '#f7768e',
          green: '#9ece6a',
          yellow: '#e0af68',
          blue: '#7aa2f7',
          magenta: '#bb9af7',
          cyan: '#7dcfff',
          white: '#a9b1d6',
          brightBlack: '#414868',
          brightRed: '#f7768e',
          brightGreen: '#9ece6a',
          brightYellow: '#e0af68',
          brightBlue: '#7aa2f7',
          brightMagenta: '#bb9af7',
          brightCyan: '#7dcfff',
          brightWhite: '#c0caf5',
        },
        scrollback: 5000,
        convertEol: true,
      })

      const fitAddon = new FitAddon()
      const webLinksAddon = new WebLinksAddon()

      terminal.loadAddon(fitAddon)
      terminal.loadAddon(webLinksAddon)
      terminal.open(terminalRef.current)

      termRef.current = terminal
      fitAddonRef.current = fitAddon

      // Initial fit
      requestAnimationFrame(() => {
        try {
          fitAddon.fit()
        } catch {
          /* ignore initial fit errors */
        }
      })

      // Send user input to WebSocket using Proxmox termproxy protocol: "0:byteLength:data"
      terminal.onData((data) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          const byteLength = new TextEncoder().encode(data).length
          wsRef.current.send(`0:${byteLength}:${data}`)
        }
      })

      // Send terminal resize events using Proxmox termproxy protocol: "1:cols:rows:"
      terminal.onResize(({ cols, rows }) => {
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(`1:${cols}:${rows}:`)
        }
      })

      // Auto-resize on container size change
      const resizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          try {
            fitAddon.fit()
          } catch {
            /* ignore resize errors */
          }
        })
      })
      resizeObserver.observe(terminalRef.current)
      resizeObserverRef.current = resizeObserver

      return () => {
        resizeObserver.disconnect()
        resizeObserverRef.current = null
        terminal.dispose()
        termRef.current = null
        fitAddonRef.current = null
        if (keepaliveTimerRef.current) {
          clearInterval(keepaliveTimerRef.current)
          keepaliveTimerRef.current = null
        }
        if (reconnectTimerRef.current) {
          clearTimeout(reconnectTimerRef.current)
          reconnectTimerRef.current = null
        }
        intentionalCloseRef.current = true
        geracaoRef.current++
        if (wsRef.current) {
          try {
            wsRef.current.close()
          } catch {
            /* ignore */
          }
          wsRef.current = null
        }
      }
    }, [])

    // Connect/reconnect when URL is available or changes
    useEffect(() => {
      if (!termRef.current || !websocketUrl) return

      // Close existing connection if reconnecting
      intentionalCloseRef.current = true
      geracaoRef.current++
      clearKeepAlive()
      clearReconnectTimer()
      if (wsRef.current) {
        try {
          wsRef.current.close()
        } catch {
          /* ignore */
        }
        wsRef.current = null
      }

      reconnectAttemptRef.current = 0
      setIdleDisconnected(false)
      intentionalCloseRef.current = false
      connect()

      return () => {
        intentionalCloseRef.current = true
        geracaoRef.current++
        clearKeepAlive()
        clearReconnectTimer()
        if (wsRef.current) {
          try {
            wsRef.current.close()
          } catch {
            /* ignore */
          }
          wsRef.current = null
        }
      }
    }, [websocketUrl]) // eslint-disable-line react-hooks/exhaustive-deps

    // Expose methods via ref
    React.useImperativeHandle(
      ref,
      () => ({
        connect,
        disconnect,
        reconnect,
        isConnected: connected,
        terminal: termRef.current,
      }),
      [connect, disconnect, reconnect, connected],
    )

    return (
      <div
        className={`serial-console relative flex flex-col ${className}`}
        style={{ width: '100%', height: '100%', backgroundColor: '#1a1b26' }}
      >
        {/* O serial só desenha o prompt depois de uma entrada, e a sessão conectada parecia
            travada (#1088). Só com a sessão CONECTADA (antes dela o Enter se perde). Faixa no
            fluxo, ACIMA do terminal: não o encobre, não recebe foco e não intercepta tecla —
            clicar nela devolve o foco ao xterm. */}
        {connected && (
          <p
            data-testid="serial-console-aviso-enter"
            onClick={() => termRef.current?.focus()}
            className="flex shrink-0 items-center gap-2 border-b border-white/10 px-3 py-1.5 text-xs text-slate-300 select-none"
          >
            <KeyboardIcon className="size-3.5 shrink-0 text-slate-400" aria-hidden="true" />
            Pressione Enter para exibir a linha de comando.
          </p>
        )}

        <div ref={terminalRef} className="min-h-0 w-full flex-1" />

        {/* Reconnecting overlay */}
        {reconnecting && !connected && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/70">
            <div className="text-center">
              <div className="mb-3 inline-block h-8 w-8 animate-spin rounded-full border-2 border-blue-400 border-t-transparent" />
              <p className="text-sm font-medium text-white">Reconectando...</p>
              <p className="mt-1 text-xs text-gray-400">
                Tentativa {reconnectAttemptRef.current}/{MAX_RECONNECT_ATTEMPTS}
              </p>
            </div>
          </div>
        )}

        {/* Disconnected overlay (after all retries failed) */}
        {!connected &&
          !reconnecting &&
          !idleDisconnected &&
          reconnectAttemptRef.current >= MAX_RECONNECT_ATTEMPTS && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/70">
              <div className="text-center">
                <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-red-500/20">
                  <span className="text-xl text-red-400">!</span>
                </div>
                <p className="text-sm font-medium text-white">Conexao perdida</p>
                <p className="mt-1 mb-4 text-xs text-gray-400">
                  Nao foi possível reconectar ao console
                </p>
                <button
                  onClick={reconnect}
                  className="rounded bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-700"
                >
                  Tentar novamente
                </button>
              </div>
            </div>
          )}

        {/* Idle timeout overlay */}
        {idleDisconnected && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/70">
            <div className="text-center">
              <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-yellow-500/20">
                <span className="text-xl text-yellow-400">&#x23F8;</span>
              </div>
              <p className="text-sm font-medium text-white">Sessao encerrada por inatividade</p>
              <p className="mt-1 mb-4 text-xs text-gray-400">
                O console foi fechado após 30 minutos sem uso
              </p>
              <button
                onClick={reconnect}
                className="rounded bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-700"
              >
                Reconectar
              </button>
            </div>
          </div>
        )}
      </div>
    )
  },
)

SerialConsole.displayName = 'SerialConsole'

export default SerialConsole
